import { useModal } from "@ebay/nice-modal-react";
import { FormProvider, useForm } from "react-hook-form";

import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import DrawerForm from "ds/components/DrawerNew/Form";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import { ConfigElement, ConfigInput, ConfigType } from "types/generated";
import { getDrawerFormFix } from "utils/css";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerBody from "ds/components/Drawer/Body";
import FormNameField from "components/Environment/components/FormItems/NameField";
import DrawerCancelButton from "ds/components/DrawerNew/CancelButton";
import Button from "ds/components/Button";
import FormValueField from "components/Environment/components/FormItems/ValueField";
import useIntentProjectConfigAdd from "shared/IntentProject/useIntentProjectConfigAdd";
import FormDescriptionField from "components/Environment/components/FormItems/DescriptionField";
import FormIsSecretField from "components/Environment/components/FormItems/IsSecretField";
import useIntentProjectConfigUpdate from "shared/IntentProject/useIntentProjectConfigUpdate";

type IntentProjectEnvironmentVariableFormDrawerProps = {
  intentProjectId: string;
  variable?: ConfigElement;
};

const IntentProjectEnvironmentVariableFormDrawer =
  createDrawer<IntentProjectEnvironmentVariableFormDrawerProps>(({ intentProjectId, variable }) => {
    const drawer = useModal();
    const isEdit = !!variable;
    const { addConfigToIntentProject, loading: addConfigLoading } = useIntentProjectConfigAdd();
    const { updateConfigToIntentProject, loading: updateConfigLoading } =
      useIntentProjectConfigUpdate();

    const variableForm = useForm<ConfigInput>({
      defaultValues: {
        id: variable?.id ?? "",
        description: variable?.description ?? "",
        type: ConfigType.EnvironmentVariable,
        writeOnly: variable?.writeOnly ?? false,
        fileMode: null,
        value: variable?.value ?? "",
      },
      shouldFocusError: true,
      mode: "onChange",
    });

    const {
      handleSubmit,
      formState: { isDirty },
      watch,
    } = variableForm;

    const onSubmit = (formData: ConfigInput) => {
      if (isEdit) {
        updateConfigToIntentProject(intentProjectId, formData, () => {
          drawer.hide();
        });
      } else {
        addConfigToIntentProject(intentProjectId, formData, () => {
          drawer.hide();
        });
      }
    };

    return (
      <DrawerForm isDirty={isDirty}>
        <FormProvider {...variableForm}>
          <form onSubmit={handleSubmit(onSubmit)} {...getDrawerFormFix()}>
            <DrawerHeader justify="between">
              <DrawerHeaderTitle
                title={isEdit ? "Edit environment variable" : "Add environment variable"}
              />
              <DrawerCloseIcon />
            </DrawerHeader>

            <DrawerBody gap="x-large" fullHeight>
              <FormNameField name="id" disabled={isEdit} />
              <FormValueField name="value" isSecret={watch("writeOnly")} />
              <FormIsSecretField
                name="writeOnly"
                description="Secret values are not accessible in the web GUI and through the API."
              />
              <FormDescriptionField name="description" minRows={4} noMargin />
            </DrawerBody>
            <DrawerFooter>
              <DrawerFooterActions>
                <DrawerCancelButton />
                <Button
                  variant="primary"
                  type="submit"
                  disabled={addConfigLoading || updateConfigLoading}
                >
                  {isEdit ? "Save" : "Create"}
                </Button>
              </DrawerFooterActions>
            </DrawerFooter>
          </form>
        </FormProvider>
      </DrawerForm>
    );
  });

export const showIntentProjectEnvironmentVariableFormDrawer = createDrawerTrigger(
  IntentProjectEnvironmentVariableFormDrawer
);
